import type { DashboardId, KriId } from '@clinspace/shared';
import { KRI_IDS, KRI_LABELS } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, heatmap, list, radar, sparkline, text, type Rect } from '../charts.ts';
import { aeDetail, deviationDetail, formDetail, queryDetail, visitDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

const RAG_COLOUR = { Green: PALETTE.forest, Amber: PALETTE.amber, Red: PALETTE.crimson };

/** Column heads for the KRI grid on the wide screen: short enough to stand level. */
const KRI_SHORT: Record<KriId, string> = {
  queryRate: 'Queries',
  saeRate: 'SAEs',
  deviationRate: 'Deviation',
  missingDataPct: 'Missing',
  visitLatenessPct: 'Late visits',
  enrollmentPace: 'Enrolment',
};

/** Risk-Based Monitoring: per-site score and RAG, KRIs breached, a stylised map, KRI radar; site -> KRI -> records. */
export class RbmDashboard extends Dashboard {
  readonly id: DashboardId = 'RiskBasedMonitoring';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('site:')) return this.view.siteLabel(segment.slice(5));
    if (segment.startsWith('kri:')) return KRI_LABELS[segment.slice(4) as KriId] ?? segment;
    return segment.replace(/^rec:/, '');
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [site, kri, rec] = this.path;
    const rbm = this.view.file.rbm;
    if (site && kri && rec) {
      const id = rec.slice(4);
      const detail = this.recordDetail(id);
      if (detail) {
        this.detail = detail;
        this.drawDetail(ctx, panel, area, detail);
      }
      return;
    }
    this.detail = undefined;
    if (site && kri) {
      const siteId = site.slice(5);
      const key = kri.slice(4) as KriId;
      const records = this.underlying(siteId, key);
      const rows = list(
        ctx,
        ['Record', 'Subject', 'Detail', 'Date'],
        records.map((r) => ({
          cells: [r.kind, r.subjectId, r.detail, r.date],
          flag: r.flag ? 'x' : undefined,
          colour: PALETTE.amber,
        })),
        area,
        {
          title: `${this.view.siteLabel(siteId)} · ${KRI_LABELS[key]} · ${records.length} records`,
          widths: [0.14, 0.16, 0.5, 0.2],
          hoveredIndex: hoveredRow(panel, 'row:'),
          rowH: 20,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `rec:${records[i]!.id}` },
          tooltip: [records[i]!.detail, 'Open the record'],
        }),
      );
      return;
    }
    if (site) {
      const siteId = site.slice(5);
      const s = rbm.sites.find((x) => x.siteId === siteId);
      if (!s) return;
      this.kpis(
        panel,
        ctx,
        [
          { label: 'Risk score', value: String(s.score), colour: RAG_COLOUR[s.rag], sub: s.rag },
          {
            label: 'KRIs breached',
            value: String(s.krisBreached),
            colour: s.krisBreached ? PALETTE.amber : PALETTE.teal,
          },
          {
            label: '12-week trend',
            value: `${s.trend > 0 ? '+' : ''}${s.trend}`,
            colour: s.trend > 5 ? PALETTE.crimson : PALETTE.teal,
          },
        ],
        { x: area.x, y: area.y, w: area.w * 0.6, h: 70 },
      );
      sparkline(
        ctx,
        s.history.map((h) => h.score),
        { x: area.x + area.w * 0.63, y: area.y + 10, w: area.w * 0.37, h: 50 },
        RAG_COLOUR[s.rag],
      );
      text(ctx, 'score history', area.x + area.w * 0.63, area.y + 70, {
        size: 9,
        colour: PALETTE.muted,
      });
      const rows = list(
        ctx,
        ['KRI', 'Value', 'Threshold', 'Risk', 'Status'],
        KRI_IDS.map((k) => {
          const v = s.kris[k];
          return {
            cells: [
              KRI_LABELS[k],
              `${v.value} ${v.unit}`,
              `${k === 'enrollmentPace' ? '<' : '>'} ${v.threshold}`,
              String(v.risk),
              v.breached ? 'BREACHED' : 'ok',
            ],
            flag: v.breached ? 'x' : undefined,
            colour: PALETTE.crimson,
          };
        }),
        { x: area.x, y: area.y + 82, w: area.w * 0.6, h: area.h - 82 },
        {
          title: 'Key risk indicators (select one for the underlying records)',
          widths: [0.4, 0.18, 0.16, 0.1, 0.16],
          hoveredIndex: hoveredRow(panel, 'kri:'),
          rowH: 22,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `kri:${i}`,
          ...r,
          data: { kind: 'drill', segment: `kri:${KRI_IDS[i]}` },
          tooltip: [
            KRI_LABELS[KRI_IDS[i]!],
            s.kris[KRI_IDS[i]!].breached
              ? 'Breached · open the records'
              : 'Within threshold · open the records',
          ],
        }),
      );
      radar(
        ctx,
        KRI_IDS.map((k) => KRI_LABELS[k].split(' (')[0]!),
        KRI_IDS.map((k) => s.kris[k].risk / 100),
        { x: area.x + area.w * 0.62, y: area.y + 82, w: area.w * 0.38, h: area.h - 82 },
        { colour: RAG_COLOUR[s.rag], title: 'KRI radar (risk 0–100)' },
      );
      return;
    }
    const sites = rbm.sites;
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'Red sites',
          value: String(sites.filter((s) => s.rag === 'Red').length),
          colour: PALETTE.crimson,
        },
        {
          label: 'Amber sites',
          value: String(sites.filter((s) => s.rag === 'Amber').length),
          colour: PALETTE.amber,
        },
        {
          label: 'Green sites',
          value: String(sites.filter((s) => s.rag === 'Green').length),
          colour: PALETTE.forest,
        },
        { label: 'KRIs breached', value: String(sites.reduce((n, s) => n + s.krisBreached, 0)) },
        {
          label: 'Mean score',
          value: String(
            Math.round((sites.reduce((n, s) => n + s.score, 0) / Math.max(1, sites.length)) * 10) /
              10,
          ),
        },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    // Stylised map: pins at normalised site coordinates.
    // On the wide centre screen the table and its KRI grid take a larger share.
    const split = this.wide > 1 ? 0.5 : 0.6;
    const map: Rect = { x: area.x, y: area.y + 88, w: area.w * split, h: area.h - 88 };
    ctx.fillStyle = PALETTE.raised;
    ctx.fillRect(map.x, map.y, map.w, map.h);
    ctx.strokeStyle = PALETTE.grid;
    for (let i = 1; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(map.x + (map.w * i) / 6, map.y);
      ctx.lineTo(map.x + (map.w * i) / 6, map.y + map.h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(map.x, map.y + (map.h * i) / 6);
      ctx.lineTo(map.x + map.w, map.y + (map.h * i) / 6);
      ctx.stroke();
    }
    text(ctx, 'Site risk map (select a pin)', map.x + 8, map.y + 16, {
      size: 11,
      colour: PALETTE.muted,
      weight: 600,
    });
    // Pins start at the site's map position and are pushed apart until none overlap.
    const pins = sites.map((s) => {
      const site = this.view.site(s.siteId);
      return {
        s,
        site,
        x: map.x + (site?.mapX ?? 0.5) * map.w,
        y: map.y + (site?.mapY ?? 0.5) * map.h,
        r: 10 + (s.score / 100) * 10,
      };
    });
    for (let iter = 0; iter < 12; iter++) {
      for (let a = 0; a < pins.length; a++) {
        for (let b = a + 1; b < pins.length; b++) {
          const pa = pins[a]!;
          const pb = pins[b]!;
          const dx = pb.x - pa.x;
          const dy = pb.y - pa.y;
          const d = Math.hypot(dx, dy) || 0.01;
          const min = pa.r + pb.r + 26;
          if (d >= min) continue;
          const push = (min - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          pa.x -= ux * push;
          pa.y -= uy * push;
          pb.x += ux * push;
          pb.y += uy * push;
        }
      }
      for (const p of pins) {
        p.x = Math.min(map.x + map.w - p.r - 8, Math.max(map.x + p.r + 8, p.x));
        p.y = Math.min(map.y + map.h - p.r - 20, Math.max(map.y + p.r + 24, p.y));
      }
    }
    pins.forEach(({ s, site, x: px, y: py, r }, i) => {
      if (!site) return;
      const hovered = panel.isHovered(`pin:${i}`);
      ctx.beginPath();
      ctx.arc(px, py, r + (hovered ? 4 : 0), 0, Math.PI * 2);
      ctx.fillStyle = RAG_COLOUR[s.rag];
      ctx.fill();
      ctx.strokeStyle = PALETTE.line;
      ctx.lineWidth = 2;
      ctx.stroke();
      text(ctx, String(Math.round(s.score)), px, py + 4, {
        size: 10,
        align: 'center',
        weight: 700,
        colour: PALETTE.onAccent,
      });
      text(ctx, site.city, px, py + r + 14, { size: 10, align: 'center', colour: PALETTE.text });
      panel.region<ViewRegion>({
        id: `pin:${i}`,
        x: px - r - 4,
        y: py - r - 4,
        w: (r + 4) * 2,
        h: (r + 4) * 2 + 14,
        data: { kind: 'drill', segment: `site:${s.siteId}` },
        tooltip: [
          this.view.siteLabel(s.siteId),
          `score ${s.score} (${s.rag}) · ${s.krisBreached} KRIs breached`,
          'Open the KRI detail',
        ],
      });
    });
    const right: Rect = {
      x: area.x + area.w * (split + 0.03),
      y: area.y + 88,
      w: area.w * (0.97 - split),
      h: area.h - 88,
    };
    const sorted = sites.slice().sort((a, b) => b.score - a.score);
    // On the wide centre screen (2026-10-07) the table takes the rows it needs and every
    // site's key risk indicators stand under it as a grid.
    const kriGrid = this.wide > 1 && right.h - (44 + sorted.length * 20) >= 40 + sorted.length * 20;
    const tableH = kriGrid ? 44 + sorted.length * 20 : right.h;
    const rows = list(
      ctx,
      ['Site', 'Score', 'RAG', 'KRIs', 'Trend'],
      sorted.map((s) => ({
        cells: [
          this.view.site(s.siteId)?.city ?? s.siteId,
          String(s.score),
          s.rag,
          String(s.krisBreached),
          `${s.trend > 0 ? '+' : ''}${s.trend}`,
        ],
        flag: s.rag !== 'Green' ? 'x' : undefined,
        colour: RAG_COLOUR[s.rag],
      })),
      { ...right, h: tableH },
      {
        title: 'Sites by risk',
        widths: [0.34, 0.16, 0.18, 0.16, 0.16],
        hoveredIndex: hoveredRow(panel, 'site:'),
        rowH: 20,
      },
    );
    rows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'drill', segment: `site:${sorted[i]!.siteId}` },
        tooltip: [this.view.siteLabel(sorted[i]!.siteId), 'Open the KRI detail'],
      }),
    );
    if (!kriGrid) return;
    const grid: Rect = {
      x: right.x,
      y: right.y + tableH + 10,
      w: right.w,
      h: right.h - tableH - 10,
    };
    text(ctx, 'Key risk indicators (risk 0–100; outlined: breached)', grid.x, grid.y + 14, {
      size: 12,
      colour: PALETTE.muted,
      weight: 600,
      maxWidth: grid.w,
    });
    const body: Rect = { x: grid.x, y: grid.y + 22, w: grid.w, h: grid.h - 22 };
    const cells = heatmap(
      ctx,
      sorted.map((s) => this.view.site(s.siteId)?.city ?? s.siteId),
      KRI_IDS.map((k) => KRI_SHORT[k]),
      (r, c) => Math.round(sorted[r]!.kris[KRI_IDS[c]!].risk),
      body,
      { max: 100, labelWidth: 96, format: (v) => String(v) },
    );
    cells.forEach((row, r) =>
      row.forEach((rect, c) => {
        const site = sorted[r]!;
        const kri = site.kris[KRI_IDS[c]!];
        if (kri.breached) {
          ctx.strokeStyle = PALETTE.amber;
          ctx.lineWidth = 2;
          ctx.strokeRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
        }
        panel.region<ViewRegion>({
          id: `kri:${r}:${c}`,
          ...rect,
          data: { kind: 'drill', segment: `site:${site.siteId}` },
          tooltip: [
            `${this.view.siteLabel(site.siteId)} · ${KRI_LABELS[KRI_IDS[c]!]}`,
            `${kri.value} ${kri.unit} against ${kri.threshold} · risk ${Math.round(kri.risk)}${kri.breached ? ' · breached' : ''}`,
            "Open the site's KRI detail",
          ],
        });
      }),
    );
  }

  private underlying(
    siteId: string,
    kri: KriId,
  ): {
    id: string;
    kind: string;
    subjectId: string;
    detail: string;
    date: string;
    flag: boolean;
  }[] {
    switch (kri) {
      case 'queryRate':
        return this.view.queries
          .filter((q) => q.siteId === siteId)
          .map((q) => ({
            id: q.id,
            kind: 'Query',
            subjectId: q.subjectId,
            detail: `${q.status} · ${q.text.slice(0, 50)}`,
            date: q.raisedAt.slice(0, 10),
            flag: q.status === 'Open',
          }));
      case 'saeRate':
        return this.view.file.adverseEvents
          .filter((a) => a.siteId === siteId && a.serious)
          .map((a) => ({
            id: a.id,
            kind: 'SAE',
            subjectId: a.subjectId,
            detail: `${a.term} · grade ${a.grade}`,
            date: a.onsetDate,
            flag: !a.expected,
          }));
      case 'deviationRate':
        return this.view.file.deviations
          .filter((d) => d.siteId === siteId)
          .map((d) => ({
            id: d.id,
            kind: 'Deviation',
            subjectId: d.subjectId,
            detail: `${d.category} · ${d.classification ?? 'unclassified'}`,
            date: d.date,
            flag: d.classification !== 'Minor',
          }));
      case 'missingDataPct':
        return this.view.file.forms
          .filter(
            (f) =>
              f.siteId === siteId && (f.status === 'Not Started' || f.missingFields.length > 0),
          )
          .map((f) => ({
            id: f.id,
            kind: 'CRF page',
            subjectId: f.subjectId,
            detail: `${f.form} ${f.visitCode} · ${f.status === 'Not Started' ? 'not started' : `${f.missingFields.length} missing`}`,
            date: f.dueDate,
            flag: f.overdue,
          }));
      case 'visitLatenessPct':
        return this.view.file.visits
          .filter((v) => v.siteId === siteId && (v.timing === 'Late' || v.status === 'Missed'))
          .map((v) => ({
            id: v.id,
            kind: 'Visit',
            subjectId: v.subjectId,
            detail: `${v.visitName} · ${v.timing}${v.deviationDays ? ` (${v.deviationDays} d)` : ''}`,
            date: v.actualDate ?? v.targetDate,
            flag: v.status === 'Missed',
          }));
      case 'enrollmentPace':
        return this.view.file.subjects
          .filter((s) => s.siteId === siteId)
          .map((s) => ({
            id: `subject:${s.id}`,
            kind: 'Subject',
            subjectId: s.id,
            detail: s.status,
            date: s.consentDate,
            flag: s.status === 'Screen Failed',
          }));
    }
  }

  private recordDetail(id: string) {
    if (id.startsWith('AE-')) {
      const a = this.view.ae(id);
      return a ? aeDetail(this.view, a) : undefined;
    }
    if (id.startsWith('PD-')) {
      const d = this.view.deviation(id);
      return d ? deviationDetail(this.view, d) : undefined;
    }
    if (id.startsWith('F-')) {
      const f = this.view.form(id);
      return f ? formDetail(this.view, f) : undefined;
    }
    if (id.startsWith('V-')) {
      const v = this.view.visit(id);
      return v ? visitDetail(this.view, v) : undefined;
    }
    if (id.startsWith('subject:')) {
      const s = this.view.subject(id.slice(8));
      return s
        ? {
            ref: this.view.ref(s.id, 'Demographics', `F-${s.id}-SCR-demographics`, 'Screening'),
            title: `Subject ${s.id}`,
            rows: [
              { label: 'Status', value: s.status, field: 'Status' },
              { label: 'Consent', value: s.consentDate, field: 'Consent date' },
              { label: 'Site', value: this.view.siteLabel(s.siteId) },
            ],
          }
        : undefined;
    }
    const q = this.view.queries.find((x) => x.id === id);
    return q ? queryDetail(this.view, q) : undefined;
  }
}
